import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Cookie, Settings, X, Check, ExternalLink } from 'lucide-react';
import { getBackendUrl } from '../services/origin';
import {
  loadConsent, saveConsent, isConsentStale, clearConsent,
} from '../services/consent';

const API = getBackendUrl();

/**
 * Bare-bones markdown-ish renderer. Handles the subset used by the
 * default cookie policy template (h1/h2, paragraphs, lists, links,
 * bold). Not a full markdown engine — by design.
 */
function renderPolicy(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 my-2 space-y-1 text-sm text-slate-700">
          {listBuf.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  const inline = (s) =>
    s
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-[#D44E20] underline">$1</a>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 rounded text-[12px]">$1</code>');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith('# ')) { flushList(); out.push(<h1 key={out.length} className="text-xl font-bold text-slate-900 mt-4 mb-2">{line.slice(2)}</h1>); continue; }
    if (line.startsWith('## ')) { flushList(); out.push(<h2 key={out.length} className="text-base font-semibold text-slate-900 mt-4 mb-1.5">{line.slice(3)}</h2>); continue; }
    if (line.startsWith('### ')) { flushList(); out.push(<h3 key={out.length} className="text-sm font-semibold text-slate-800 mt-3 mb-1">{line.slice(4)}</h3>); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) { listBuf.push(line.slice(2)); continue; }
    flushList();
    out.push(<p key={out.length} className="text-sm text-slate-700 leading-relaxed my-1.5" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
  }
  flushList();
  return out;
}

/**
 * Public cookie consent banner.
 *
 * Props:
 *   brandId   string  — required to fetch policy + version
 *   onClose   func    — optional callback when user dismisses
 *
 * Shows automatically when:
 *   - no consent stored, OR
 *   - server's cookie_consent_version is greater than stored version.
 * Footer link "Gestisci cookie" can force re-open from outside via the
 * exported `openCookieSettings()` helper (window event).
 */
export default function CookieConsentBanner({ brandId }) {
  const [config, setConfig] = useState(null);  // { cookie_policy_md, cookie_consent_version }
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('compact'); // 'compact' | 'detail' | 'policy'
  const [stats, setStats] = useState(true);
  const [mkt, setMkt] = useState(true);

  // 1. Load config from server
  useEffect(() => {
    if (!brandId) return;
    let alive = true;
    axios.get(`${API}/api/public/tracking/${brandId}`).then(r => {
      if (!alive) return;
      setConfig(r.data);
      const existing = loadConsent();
      if (!existing || isConsentStale(r.data.cookie_consent_version)) {
        setOpen(true);
      } else {
        setStats(!!existing.statistics);
        setMkt(!!existing.marketing);
      }
    }).catch(() => {
      // Brand fetch failed — still show banner with default policy so
      // visitors aren't tracked without consent in any case.
      setConfig({ cookie_policy_md: '', cookie_consent_version: 1 });
      if (!loadConsent()) setOpen(true);
    });
    return () => { alive = false; };
  }, [brandId]);

  // 2. External trigger via "Gestisci cookie" footer link
  useEffect(() => {
    const handler = () => { setMode('detail'); setOpen(true); };
    window.addEventListener('lazo:open-cookie-settings', handler);
    return () => window.removeEventListener('lazo:open-cookie-settings', handler);
  }, []);

  const decide = useCallback((choice) => {
    if (!config) return;
    saveConsent(choice, config.cookie_consent_version || 1);
    setStats(!!choice.statistics);
    setMkt(!!choice.marketing);
    setOpen(false);
    setMode('compact');
  }, [config]);

  const acceptAll = () => decide({ statistics: true, marketing: true });
  const onlyNecessary = () => decide({ statistics: false, marketing: false });
  const saveCustom = () => decide({ statistics: stats, marketing: mkt });

  if (!open || !config) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-5 pointer-events-none" data-testid="cookie-consent-banner">
      <div className="pointer-events-auto max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Compact mode */}
        {mode === 'compact' && (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Cookie size={18} className="text-amber-700" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900">Rispettiamo la tua privacy</h2>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  Usiamo i cookie per migliorare la tua esperienza, misurare il traffico (Google Analytics) e mostrarti annunci pertinenti su Meta, TikTok e Google. Puoi accettare tutto, solo i cookie necessari o personalizzare la scelta.{' '}
                  <button
                    onClick={() => setMode('policy')}
                    data-testid="cookie-show-policy-btn"
                    className="text-[#D44E20] hover:underline font-medium inline-flex items-center gap-0.5"
                  >
                    Cookie policy <ExternalLink size={11} />
                  </button>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setMode('detail')}
                data-testid="cookie-customize-btn"
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5"
              >
                <Settings size={14} /> Personalizza
              </button>
              <button
                onClick={onlyNecessary}
                data-testid="cookie-only-necessary-btn"
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Solo necessari
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all-btn"
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#D44E20] hover:bg-[#B33F18] text-sm font-semibold text-white inline-flex items-center justify-center gap-1.5"
              >
                <Check size={14} /> Accetta tutto
              </button>
            </div>
          </div>
        )}

        {/* Detail / customize mode */}
        {mode === 'detail' && (
          <div>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
                <Settings size={16} className="text-slate-500" /> Personalizza i cookie
              </h2>
              <button
                onClick={() => setMode('compact')}
                data-testid="cookie-back-btn"
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {/* Necessary — locked */}
              <ConsentRow
                title="Necessari (sempre attivi)"
                description="Funzionali al sito: login, sicurezza, sessione, carrello. Non possono essere disattivati."
                locked
                value={true}
                testid="cookie-cat-necessary"
              />
              <ConsentRow
                title="Statistiche"
                description="Google Analytics 4. Ci permette di capire come usi il sito (pagine viste, sessioni). Dati aggregati e anonimi."
                value={stats}
                onChange={setStats}
                testid="cookie-cat-statistics"
              />
              <ConsentRow
                title="Marketing"
                description="Meta Pixel, TikTok Pixel, Google Ads. Misurano l'efficacia delle nostre campagne e abilitano il retargeting sulle piattaforme social."
                value={mkt}
                onChange={setMkt}
                testid="cookie-cat-marketing"
              />
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setMode('policy')}
                className="text-sm text-slate-500 hover:text-[#D44E20] underline mr-auto self-center"
                data-testid="cookie-show-policy-btn-detail"
              >
                Cookie policy completa
              </button>
              <button
                onClick={onlyNecessary}
                data-testid="cookie-only-necessary-btn-detail"
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Solo necessari
              </button>
              <button
                onClick={saveCustom}
                data-testid="cookie-save-custom-btn"
                className="px-4 py-2 rounded-xl bg-[#D44E20] hover:bg-[#B33F18] text-sm font-semibold text-white"
              >
                Salva preferenze
              </button>
            </div>
          </div>
        )}

        {/* Policy mode */}
        {mode === 'policy' && (
          <div>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Cookie policy</h2>
              <button
                onClick={() => setMode('compact')}
                data-testid="cookie-policy-back-btn"
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto" data-testid="cookie-policy-content">
              {renderPolicy(config.cookie_policy_md)}
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setMode('compact')}
                className="px-4 py-2 rounded-xl bg-slate-100 text-sm font-semibold text-slate-700"
              >
                Indietro
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ title, description, value, onChange, locked, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-start gap-3" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        disabled={locked}
        onClick={() => !locked && onChange && onChange(!value)}
        data-testid={`${testid}-toggle`}
        className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${value ? 'bg-[#D44E20]' : 'bg-slate-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

/* ── Helper: programmatic re-open from anywhere (footer link etc) ── */

export function openCookieSettings() {
  // Allow re-open even after consent saved
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('lazo:open-cookie-settings'));
  }
}

/* ── Helper: wipe consent (e.g. "revoke all"). Forces banner. ────── */

export function revokeCookieConsent() {
  clearConsent();
  openCookieSettings();
}
